import { liveReload } from '@shared/services/realtime.service';
import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { FormsModule } from '@angular/forms';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';

import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';

import { ModulesService } from '@shared/services/modules.service';
import { IncidentsService } from '@shared/services/entities/incidents.service';
import { HospitalServicesService } from '@shared/services/entities/hospitalServices.service';
import { UsersService } from '@shared/services/entities/users.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { DoctorsService } from '@shared/services/entities/doctors.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { Incident, INCIDENT_TYPES, INCIDENT_STATUSES, incidentStatus } from '@shared/models/entities/incident.model';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { IncidentFormDialogComponent } from './incident-form-dialog/incident-form-dialog.component';
import { IncidentMpxrDialogComponent } from './incident-mpxr-dialog.component';
import { GaFormsService } from '@shared/services/entities/ga-forms.service';
import { GaPdfService } from '@app/shared/pdf/ga-pdf.service';
import { buildIncidentDoc } from '@app/shared/pdf/ga-bitacoras';

/**
 * Módulo de Incidencias — los procesos "Incidencia – Insumos" e
 * "Incidencia – Equipo/Instrumental" del forms Medallia, con seguimiento
 * (REPORTADA → EN REVISION → RESUELTA). La consulta histórica también vive
 * en Reportes → Incidencias.
 */
@Component({
  selector: 'app-incidents',
  standalone: true,
  imports: [KpiStripComponent, MatCheckboxModule, MatButtonToggleModule, SelectionBarComponent, PageLoaderComponent, MatDatepickerModule, FormsModule, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatMenuModule,
  ],
  templateUrl: './incidents.component.html',
  styleUrl: './incidents.component.scss',
})
export class IncidentsComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/incidents', 'Incidencias');

  private readonly incidentsService        = inject(IncidentsService);
  private readonly hospitalServicesService = inject(HospitalServicesService);
  private readonly usersService            = inject(UsersService);
  private readonly placesService           = inject(PlacesService);
  private readonly simpleExcel             = inject(SimpleExcelService);
  private readonly doctorsService          = inject(DoctorsService);
  private placesById: Record<string, string> = {};
  private doctorsById: Record<string, string> = {};
  private readonly customMessageService    = inject(CustomMessageService);
  private readonly dialog                  = inject(MatDialog);
  private readonly userScopeService        = inject(UserScopeService);
  private readonly gaForms                 = inject(GaFormsService);
  private readonly gaPdf                   = inject(GaPdfService);
  /** id de la incidencia cuya bitácora se está generando. */
  printing = signal<string | null>(null);
  private scope: UserScope = { places: null, specialties: null };

  loading = this.incidentsService.loading;

  readonly types    = INCIDENT_TYPES;
  readonly statuses = INCIDENT_STATUSES;

  /** Filtros de la lista. */
  /** Periodo de la lista: por omisión el mes actual; se amplía con el filtro. */
  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();

  typeFilter:   string = '';
  statusFilter: string = '';

  services: any[] = [];
  private servicesById: Record<string, string> = {};
  private reporterName = '';

  // ─── Indicadores (como en el inventario) ───
  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI_FILTERS: Record<string, (r: any) => boolean> = {
    justified:   r => r?.justified === true,
    unjustified: r => r?.justified !== true,
    mpxr:        r => !!r?.mpxr,
    nompxr:      r => !r?.mpxr,
    high:     r => String(r?.priority ?? '').toUpperCase() === 'ALTA',
    replace:  r => r?.needsReplacement === true,
    quality:  r => r?.qualityClass === 'CALIDAD',
  };
  private refreshKpis(): void {
    const all = this.allRows;
    const count = (k: string) => all.filter(IncidentsComponent.KPI_FILTERS[k]).length;
    this.kpis = [
      { key: 'total', label: 'Incidencias', icon: 'report_problem', value: all.length, isTotal: true },
      { key: 'justified', label: 'Justificadas', icon: 'verified', value: count('justified'), tone: 'ok' },
      { key: 'unjustified', label: 'Sin justificar', icon: 'gpp_maybe', value: count('unjustified'), tone: 'warn' },
      { key: 'mpxr', label: 'Con MPXR', icon: 'tag', value: count('mpxr') },
      { key: 'nompxr', label: 'Sin MPXR', icon: 'label_off', value: count('nompxr') },
      { key: 'high', label: 'Prioridad alta', icon: 'priority_high', value: count('high'), tone: 'danger' },
      { key: 'replace', label: 'Requieren remplazo', icon: 'swap_horiz', value: count('replace') },
      { key: 'quality', label: 'De calidad', icon: 'verified_user', value: count('quality'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(key: string | null): void { this.activeKpi = key; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? IncidentsComponent.KPI_FILTERS[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  /** Vista tabla / tarjetas y selección con checkbox. */
  readonly listView = new ListView('incidents', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);

  columns = ['select', 'folio', 'createdOn', 'type', 'place', 'doctor', 'reference', 'piece', 'priority', 'quality', 'mpxr', 'justified', 'reportedByName', 'actions'];

  // ─── Calidad (modelo) y resumen por lote / código ───
  private backfilled = false;
  showLots = true;
  lotSummary: { code: string; description: string; batch: string; total: number; open: number; quality: number; hospitals: string }[] = [];
  private buildLotSummary(rows: any[]): void {
    const groups = new Map<string, any>();
    for (const r of rows) {
      if (!r.productCode && !r.batch) continue;
      const k = `${r.productCode ?? ''}|${r.batch ?? ''}`;
      const g = groups.get(k) ?? { code: r.productCode || '—', description: r.productDescription || r.equipmentName || '',
                                   batch: r.batch || 'Sin lote', total: 0, open: 0, quality: 0, places: new Set<string>() };
      g.total++;
      if (!r.justified) g.open++;
      if (r.qualityClass === 'CALIDAD') g.quality++;
      if (r._place && r._place !== '—') g.places.add(r._place);
      groups.set(k, g);
    }
    this.lotSummary = [...groups.values()]
      .map(g => ({ ...g, hospitals: [...g.places].join(', ') }))
      .sort((a, b) => b.total - a.total || b.quality - a.quality || a.code.localeCompare(b.code));
  }
  filterByLot(l: { code: string; batch: string }): void {
    this.applySearch(l.batch !== 'Sin lote' ? l.batch : l.code);
  }

  async setQuality(i: any, quality: 'CALIDAD' | 'NO CALIDAD'): Promise<void> {
    if (!this.canJustify() || this.reviewing() || i.qualityClass === quality && i.qualitySource === 'MANUAL') return;
    await this.saveReview(i, { quality }, `Clasificada como <b>${quality}</b>. El modelo aprende de esta corrección.`);
  }

  /** MPXR: Representante o Administrador Epione / Medtronic. Justificado: Mesa de control o
   *  Administrador Epione / Medtronic. El API lo vuelve a validar. */
  canMpxr = signal(false);
  canJustify = signal(false);
  reviewing = signal<string | null>(null);

  async editMpxr(i: any): Promise<void> {
    if (!this.canMpxr() || this.reviewing()) return;
    const ref = this.dialog.open(IncidentMpxrDialogComponent, { data: { folio: i.folio, mpxr: i.mpxr ?? '' }, width: '380px', maxWidth: '95vw' });
    const value = await new Promise<string | undefined>(res => ref.afterClosed().subscribe(res));
    if (value === undefined || value === (i.mpxr ?? '')) return;
    await this.saveReview(i, { mpxr: value || null }, value ? `MPXR <b>${value}</b> guardado.` : 'MPXR eliminado.');
  }

  async toggleJustified(i: any): Promise<void> {
    if (!this.canJustify() || this.reviewing()) return;
    const next = !i.justified;
    const ok = await this.customMessageService.open({
      title: next ? 'Marcar como justificada' : 'Quitar justificación', type: 'warning',
      message: `¿${next ? 'Marcar' : 'Quitar'} la incidencia <b>${i.folio}</b> como justificada?`,
      acceptBtn: true, acceptBtnText: next ? 'Justificar' : 'Quitar', dismissBtn: true, dismissBtnText: 'Cancelar',
    });
    if (!ok) return;
    await this.saveReview(i, { justified: next }, next ? 'Incidencia justificada.' : 'Se quitó la justificación.');
  }

  private async saveReview(i: any, change: { mpxr?: string | null; justified?: boolean; quality?: 'CALIDAD' | 'NO CALIDAD' }, done: string): Promise<void> {
    this.reviewing.set(i.id);
    try {
      await this.incidentsService.review(i.id, change);
      await this.customMessageService.open({ title: 'Listo', type: 'success', message: done, duration: 2000 });
      await this.loadIncidents();
    } catch (err: any) {
      await this.customMessageService.open({ title: 'Error', type: 'error', dismissBtn: true,
        message: err?.message || 'No se pudo guardar.' });
    } finally {
      this.reviewing.set(null);
    }
  }
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['incidents'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadIncidents()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    this.loadServices();      // best-effort, para el select del diálogo
    this.loadReporter();      // best-effort, nombre del usuario logueado
    try { this.scope = await this.userScopeService.load(); } catch { /* sin alcance */ }
    const roles = (this.scope.roles ?? []).map(r => String(r).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim());
    const admin = roles.some(r => r === 'ADMINISTRADOR EPIONE' || r === 'ADMINISTRADOR MEDTRONIC');
    this.canMpxr.set(admin || roles.some(r => r.includes('REPRESENTANTE')));
    this.canJustify.set(admin || roles.some(r => r.startsWith('MESA DE CONTROL')));
    await this.loadNames();   // hospital y médico en la tabla
    await this.loadIncidents();
  }

  /** Nombres de hospitales (alias) y médicos para la tabla. */
  private async loadNames(): Promise<void> {
    const cond = { searchType: 'default', condition: { deleted: false } };
    try {
      const [p, d]: any[] = await Promise.all([this.placesService.search(cond), this.doctorsService.search(cond)]);
      this.placesById = Object.fromEntries(((p?.data ?? []) as any[]).map(x => [x.id, x.alias || x.name || '']));
      this.doctorsById = Object.fromEntries(((d?.data ?? []) as any[]).map(x => [x.id, x.name || '']));
    } catch { /* la tabla muestra — */ }
  }

  private async loadServices(): Promise<void> {
    try {
      await this.hospitalServicesService.search({ searchType: 'default', condition: { deleted: false, active: true } });
      this.services = this.hospitalServicesService.listData();
      this.servicesById = Object.fromEntries(
        this.services.map((s: any) => [s.id, s.epioneCode || s.mdtCode || s.name || ''])
      );
    } catch {
      console.warn('[Incidencias] No se pudieron cargar los servicios hospitalarios');
    }
  }

  private async loadReporter(): Promise<void> {
    try {
      const user = await this.usersService.loadCurrentUser();
      this.reporterName = user?.name ?? '';
    } catch { /* sin nombre: se guarda vacío */ }
  }


  /** Búsqueda de texto sobre la tabla. */
  searchText = '';
  applySearch(value: string): void {
    this.searchText = value ?? '';
    this.dataSource.filter = this.searchText.trim().toLowerCase();
  }

  get filterChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.typeFilter) c.push({ key: 'type', label: 'Tipo', value: this.typeFilter });

    if (this.periodStart || this.periodEnd) {
      const f = (d: Date | null) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '…';
      c.push({ key: 'period', label: 'Periodo', value: f(this.periodStart) + ' – ' + f(this.periodEnd), removable: false });
    }
    return c;
  }
  async removeChip(key: string): Promise<void> {
    if (key === 'type') this.typeFilter = '';
    if (key === 'status') this.statusFilter = '';
    await this.loadIncidents();
  }
  async clearFilters(): Promise<void> {
    this.typeFilter = ''; this.statusFilter = '';
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    this.applySearch('');
    await this.loadIncidents();
  }

  async loadIncidents(): Promise<void> {
    try {
      const condition: any = { deleted: false };
      if (this.typeFilter)   condition.type = this.typeFilter;
      // El estado se filtra aquí (los registros viejos traen REPORTADA / EN REVISION / RESUELTA).

      const response: any = await this.incidentsService.search({ searchType: 'default', condition });
      const rows = (response?.data ?? []).map((i: any) => ({
        ...i,
        // Folio/Remisión (insumos) y la pieza (equipo, instrumental, insumo o accesorio).
        _reference: i.interventionFolio || '—',
        _piece: [i.equipmentName, i.serialNumber, i.tagRfid, i.batch ? 'Lote ' + i.batch : ''].filter(Boolean).join(' · ') || '—',
        _service: this.servicesById[i.hospitalService] ?? '',
        _place: this.placesById[i.place] ?? '—',
        _doctor: this.doctorsById[i.doctor] ?? '—',
        status: incidentStatus(i.status),
      }))
        .filter((r: any) => inDateRange(r.createdOn, this.periodStart, this.periodEnd))
        // Alcance del usuario: solo sus hospitales (sin hospital = se muestra).
        .filter((r: any) => inScope(this.scope.places, r.place));
      rows.sort((a: any, b: any) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.allRows = rows;
      this.buildLotSummary(rows);
      this.refreshKpis();
      // Incidencias de antes del modelo: se clasifican una vez (y se completa código / lote).
      if (!this.backfilled && rows.some((r: any) => !r.qualityClass)) {
        this.backfilled = true;
        this.incidentsService.backfill().then(n => { if (n) void this.loadIncidents(); }).catch(() => { /* sin cambios */ });
      }
      this.dataSource.paginator = this.paginator;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar las incidencias.', dismissBtn: true,
      });
    }
  }

  openCreate(): void {
    this.openFormDialog();
  }

  openEdit(incident: Incident): void {
    this.openFormDialog(incident);
  }

  private openFormDialog(incident?: Incident): void {
    const ref = this.dialog.open(IncidentFormDialogComponent, {
      data: { incident, services: this.services, reporterName: this.reporterName },
      width: '680px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) this.loadIncidents();
    });
  }

  statusClass(status: string): string {
    switch (status) {
      case 'RESUELTO':    return 'chip chip--ok';
      default:            return 'chip chip--open';
    }
  }

  priorityClass(priority: string | null): string {
    switch (priority) {
      case 'ALTA':  return 'chip chip--high';
      case 'MEDIA': return 'chip chip--warn';
      case 'BAJA':  return 'chip chip--ok';
      default:      return '';
    }
  }

  /** Descarga en Excel lo que se ve en la tabla (con los filtros aplicados). */
  /** Formato GA "BITACORA · INCIDENCIA" (GA-Bitacoras.pdf), con los datos que arma el API. */
  async printBitacora(i: any): Promise<void> {
    if (!i?.id || this.printing()) return;
    this.printing.set(i.id);
    try {
      const data = await this.gaForms.incident(i.id);
      this.gaPdf.download(buildIncidentDoc(data), `Bitacora-incidencia-${data.registro || i.folio || ''}`);
    } catch (err: any) {
      await this.customMessageService.open({ title: 'Error', type: 'error', dismissBtn: true,
        message: err?.message || 'No se pudo generar la bitácora.' });
    } finally {
      this.printing.set(null);
    }
  }

  async downloadExcel(selection?: any[]): Promise<void> {
    const rows = (selection ?? this.dataSource.filteredData ?? []) as any[];
    const columns = [
      { columnKey: 'folio', columnName: 'Folio' },
      { columnKey: '_date', columnName: 'Fecha' },
      { columnKey: 'type', columnName: 'Tipo' },
      { columnKey: '_place', columnName: 'Hospital' },
      { columnKey: '_doctor', columnName: 'Médico' },
      { columnKey: '_serviceName', columnName: 'CIR (servicio)' },
      { columnKey: '_reference', columnName: 'Folio/Remisión' },
      { columnKey: '_piece', columnName: 'Pieza' },
      { columnKey: 'reason', columnName: 'Razón' },
      { columnKey: '_priority', columnName: 'Prioridad' },
      { columnKey: '_replacement', columnName: '¿Necesita remplazo?' },
      { columnKey: 'description', columnName: 'Descripción' },

      { columnKey: 'resolution', columnName: 'Resolución' },
      { columnKey: 'productCode', columnName: 'Código' },
      { columnKey: 'batch', columnName: 'Lote' },
      { columnKey: '_quality', columnName: 'Clasificación' },
      { columnKey: 'mpxr', columnName: 'MPXR' },
      { columnKey: '_justified', columnName: 'Justificado' },
      { columnKey: '_photos', columnName: 'Fotos' },
      { columnKey: 'reportedByName', columnName: 'Reportó' },
    ].map(c => ({ ...c, type: 'string' as const }));
    const data = rows.map(r => ({
      ...r,
      _date: this.formatDate(r.createdOn),
      _serviceName: this.servicesById[r.hospitalService] ?? '',
      _priority: r.priority ?? '',
      _replacement: r.needsReplacement == null ? '' : (r.needsReplacement ? 'Sí' : 'No'),
      _justified: r.justified ? 'Sí' : 'No',
      _quality: r.qualityClass ? `${r.qualityClass} (${r.qualitySource === 'MANUAL' ? 'manual' : Math.round((r.qualityScore || 0) * 100) + '%'})` : '',
      _photos: (r.photos ?? []).length,
    }));
    await this.simpleExcel.simpleXLSX({
      fileName: `Incidencias${selection ? ' (selección)' : ''} ${new Date().toISOString().slice(0, 10)}`,
      sheets: [{ sheetName: 'Incidencias', columns: columns as any, data, header: [], lineBreak: 0 }],
    });
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  }
}
